/**
 * Price Intelligence Components
 * 
 * NON-NEGOTIABLE RULES:
 * - Locked data only (PDF, PO, Xero bills, bank records)
 * - No recommendations (show trends, never suggest prices)
 * - Full source traceability (every insight shows source document)
 * - Category-first intelligence
 */
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, AlertTriangle, Info, FileText,
  Calendar, Building2, DollarSign, BarChart3, ChevronRight,
  ArrowUpRight, ArrowDownRight, Minus, ExternalLink, Clock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ SOURCE BADGE ============

export function PriceSourceBadge({ sourceType }) {
  const configs = {
    SUPPLIER_QUOTE_PDF: { color: 'rgb(59, 130, 246)', label: 'PDF Quote' },
    PURCHASE_ORDER: { color: 'rgb(249, 115, 22)', label: 'PO' },
    XERO_BILL: { color: 'rgb(14, 165, 233)', label: 'Xero Bill' },
    BANK_TRANSACTION: { color: 'rgb(168, 85, 247)', label: 'Bank' },
    ACCEPTED_QUOTE: { color: 'rgb(34, 197, 94)', label: 'Quote' }
  };

  const config = configs[sourceType] || { color: 'var(--text-muted)', label: sourceType };

  return (
    <span
      data-testid={`source-badge-${sourceType}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 6px',
        borderRadius: '4px',
        background: `${config.color}20`,
        color: config.color,
        fontSize: '0.625rem',
        fontWeight: 500
      }}
    >
      <FileText size={10} />
      {config.label}
    </span>
  );
}

// ============ RISK LEVEL BADGE ============

export function RiskLevelBadge({ level }) {
  const configs = {
    LOW: { color: 'var(--status-success)', label: 'Low Risk' },
    MEDIUM: { color: 'var(--status-warning)', label: 'Medium Risk' },
    HIGH: { color: 'var(--status-error)', label: 'High Risk' }
  };

  const config = configs[level] || configs.MEDIUM;

  return (
    <span
      data-testid={`risk-badge-${level}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 8px',
        borderRadius: '4px',
        background: `${config.color}20`,
        color: config.color,
        fontSize: '0.6875rem',
        fontWeight: 600
      }}
    >
      {level === 'HIGH' && <AlertTriangle size={12} />}
      {config.label}
    </span>
  );
}

// ============ CATEGORY CARD ============

export function CategoryCard({ category, onClick }) {
  const hasData = category.record_count > 0;

  return (
    <div
      data-testid={`category-card-${category.category}`}
      onClick={() => onClick && onClick(category.category)}
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all var(--transition-base)'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-sm)' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>{category.label}</h4>
          <p style={{ margin: '4px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            {category.record_count} records • {category.supplier_count} supplier(s)
          </p>
        </div>
        {onClick && <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />}
      </div>

      {hasData && category.price_range && (
        <div style={{ display: 'flex', gap: 'var(--space-lg)', marginTop: 'var(--space-sm)' }}>
          <div>
            <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Low</div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--status-success)' }}>
              £{(category.price_range.low || 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>High</div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--status-error)' }}>
              £{(category.price_range.high || 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {!hasData && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          No price data yet
        </div>
      )}
    </div>
  );
}

// ============ ALLOWANCE DRIFT ALERT ============

export function AllowanceDriftAlert({ drift }) {
  const [expanded, setExpanded] = useState(false);

  const severityColors = {
    HIGH: 'var(--status-error)',
    MEDIUM: 'var(--status-warning)',
    LOW: 'var(--text-muted)'
  };

  return (
    <div
      data-testid={`drift-alert-${drift.category}`}
      style={{
        background: `${severityColors[drift.severity]}10`,
        border: `1px solid ${severityColors[drift.severity]}30`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        marginBottom: 'var(--space-sm)'
      }}
    >
      <div 
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
          <AlertTriangle size={18} style={{ color: severityColors[drift.severity], flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{drift.category_label}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {drift.message}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <RiskLevelBadge level={drift.severity} />
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            {drift.occurrences} occurrences
          </div>
        </div>
      </div>

      {expanded && drift.examples && drift.examples.length > 0 && (
        <div style={{ marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--glass-border)' }}>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-sm)', textTransform: 'uppercase' }}>
            Examples
          </div>
          {drift.examples.map((example, idx) => (
            <div 
              key={idx}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: 'var(--space-xs) 0',
                borderBottom: idx < drift.examples.length - 1 ? '1px dashed var(--glass-border)' : 'none',
                fontSize: '0.75rem'
              }}
            >
              <span style={{ color: 'var(--text-secondary)' }}>{example.job_title}</span>
              <span>
                <span style={{ color: 'var(--text-muted)' }}>Allowed:</span> £{example.quote_allowance?.toFixed(2)} 
                <span style={{ margin: '0 4px' }}>→</span>
                <span style={{ color: 'var(--status-error)' }}>Actual:</span> £{example.actual_cost?.toFixed(2)}
                <span style={{ color: severityColors[drift.severity], marginLeft: '8px' }}>
                  (+{example.drift_percentage}%)
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ PRICE RECORD ROW ============

export function PriceRecordRow({ record }) {
  return (
    <tr data-testid={`price-record-${record.id}`}>
      <td style={{ padding: 'var(--space-sm)', fontSize: '0.8125rem' }}>
        <div>{record.description}</div>
        {record.material_specification && (
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            {record.material_specification}
          </div>
        )}
      </td>
      <td style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: 500 }}>
        {record.unit_price ? `£${record.unit_price.toFixed(2)}` : '—'}
      </td>
      <td style={{ padding: 'var(--space-sm)', textAlign: 'right' }}>
        {record.line_total ? `£${record.line_total.toFixed(2)}` : '—'}
      </td>
      <td style={{ padding: 'var(--space-sm)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Building2 size={12} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '0.75rem' }}>{record.source_supplier || 'Unknown'}</span>
        </div>
      </td>
      <td style={{ padding: 'var(--space-sm)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Calendar size={12} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '0.75rem' }}>
            {record.price_date ? new Date(record.price_date).toLocaleDateString('en-GB') : '—'}
          </span>
        </div>
      </td>
      <td style={{ padding: 'var(--space-sm)' }}>
        <PriceSourceBadge sourceType={record.source_type} />
      </td>
    </tr>
  );
}

// ============ SUPPLIER COMPARISON TABLE ============

export function SupplierComparisonTable({ comparison }) {
  if (!comparison || !comparison.suppliers || comparison.suppliers.length === 0) {
    return (
      <div style={{ padding: 'var(--space-lg)', textAlign: 'center', color: 'var(--text-muted)' }}>
        No supplier comparison data available
      </div>
    );
  }

  return (
    <div data-testid="supplier-comparison-table">
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
        Price range: £{comparison.price_range_low?.toFixed(2)} — £{comparison.price_range_high?.toFixed(2)}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Supplier</th>
              <th style={{ padding: 'var(--space-sm)', textAlign: 'right', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Price</th>
              <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Date</th>
              <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Source</th>
            </tr>
          </thead>
          <tbody>
            {comparison.suppliers.map((supplier, idx) => (
              <tr key={idx}>
                <td style={{ padding: 'var(--space-sm)', fontWeight: 500 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Building2 size={14} style={{ color: 'var(--accent-primary)' }} />
                    {supplier.supplier}
                  </div>
                </td>
                <td style={{ padding: 'var(--space-sm)', textAlign: 'right', fontWeight: 600, fontSize: '1rem' }}>
                  £{supplier.price?.toFixed(2)}
                </td>
                <td style={{ padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {supplier.date ? new Date(supplier.date).toLocaleDateString('en-GB') : '—'}
                </td>
                <td style={{ padding: 'var(--space-sm)' }}>
                  <PriceSourceBadge sourceType={supplier.source_type} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ 
        marginTop: 'var(--space-sm)', 
        padding: 'var(--space-sm)', 
        background: 'rgba(59, 130, 246, 0.05)', 
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.6875rem',
        color: 'var(--text-muted)'
      }}>
        <Info size={12} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
        Comparison shown for reference only. No supplier ranking or recommendation implied.
      </div>
    </div>
  );
}

// ============ DATA SOURCES SUMMARY ============

export function DataSourcesSummary({ sources, dateRange }) {
  const sourceLabels = {
    SUPPLIER_QUOTE_PDF: 'PDF Quotes',
    PURCHASE_ORDER: 'Purchase Orders',
    XERO_BILL: 'Xero Bills',
    BANK_TRANSACTION: 'Bank Transactions',
    ACCEPTED_QUOTE: 'Accepted Quotes'
  };

  const total = Object.values(sources || {}).reduce((a, b) => a + b, 0);

  return (
    <div 
      data-testid="data-sources-summary"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)'
      }}
    >
      <h4 style={{ margin: '0 0 var(--space-md)', fontSize: '0.875rem' }}>Data Sources</h4>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-sm)' }}>
        {Object.entries(sources || {}).map(([source, count]) => (
          <div 
            key={source}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-xs) var(--space-sm)',
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-sm)'
            }}
          >
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {sourceLabels[source] || source}
            </span>
            <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{count}</span>
          </div>
        ))}
      </div>

      <div style={{ 
        marginTop: 'var(--space-md)', 
        paddingTop: 'var(--space-md)', 
        borderTop: '1px solid var(--glass-border)',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.75rem',
        color: 'var(--text-muted)'
      }}>
        <span>Total: {total} records</span>
        {dateRange?.earliest && dateRange?.latest && (
          <span>
            <Clock size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            {new Date(dateRange.earliest).toLocaleDateString('en-GB')} — {new Date(dateRange.latest).toLocaleDateString('en-GB')}
          </span>
        )}
      </div>
    </div>
  );
}

export default {
  PriceSourceBadge,
  RiskLevelBadge,
  CategoryCard,
  AllowanceDriftAlert,
  PriceRecordRow,
  SupplierComparisonTable,
  DataSourcesSummary
};
